import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ResetCategories,
  PreviewResponse,
  PreviewApiResponse,
  ExecuteResponse,
  ExecuteApiResponse,
  ResetHistoryResponse,
  HistoryApiResponse,
} from '../models/reset-integration.model';

/**
 * Reset Integration Service
 *
 * Handles API communication for the reset integration data feature
 * in the admin portal. This feature enables super admin users to reset
 * test/development data from Zoho FSM, Zoho Desk, and Firebase Auth.
 *
 * All endpoints require superAdmin claim validation.
 *
 * @see agent-os/specs/2026-01-25-reset-integration-data/spec.md
 */
@Injectable({
  providedIn: 'root',
})
export class ResetIntegrationService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Preview reset operation before execution
   * POST /admin/reset-integration/preview
   *
   * Returns counts of records that will be affected per category,
   * protected records that will be skipped, estimated duration,
   * and any applicable warnings.
   *
   * @param categories The category configuration specifying which data types to reset
   * @returns Preview response with counts, protected records, and warnings
   * @throws Error if the API call fails or returns an error response
   */
  async preview(categories: ResetCategories): Promise<PreviewResponse> {
    const response = await firstValueFrom(
      this.http.post<PreviewApiResponse>(
        `${this.apiUrl}/admin/reset-integration/preview`,
        { categories }
      )
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to generate reset preview');
    }

    return response.data;
  }

  /**
   * Execute reset operation
   * POST /admin/reset-integration/execute
   *
   * Performs the actual deletion of records in Zoho FSM, Zoho Desk,
   * and Firebase Auth. Clears corresponding Zoho ID fields in the Hub database.
   * Rate limited to 1 request per minute per user.
   *
   * @param categories The category configuration specifying which data types to reset
   * @param confirmationPhrase Must be exactly "RESET INTEGRATION DATA" (case-sensitive)
   * @returns Execute response with results, errors, and audit event ID
   * @throws Error if the API call fails, confirmation phrase is invalid, or rate limit exceeded
   */
  async execute(
    categories: ResetCategories,
    confirmationPhrase: string
  ): Promise<ExecuteResponse> {
    const response = await firstValueFrom(
      this.http.post<ExecuteApiResponse>(
        `${this.apiUrl}/admin/reset-integration/execute`,
        { categories, confirmationPhrase }
      )
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to execute reset operation');
    }

    return response.data;
  }

  /**
   * Get reset operation history
   * GET /admin/reset-integration/history
   *
   * Returns a paginated list of past reset operations with
   * executor name, categories, status, and summary totals.
   *
   * @param page Page number (default 1)
   * @param limit Number of items per page (default 25)
   * @returns Paginated list of reset history items
   * @throws Error if the API call fails or returns an error response
   */
  async getHistory(page = 1, limit = 25): Promise<ResetHistoryResponse> {
    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('limit', limit.toString());

    const response = await firstValueFrom(
      this.http.get<HistoryApiResponse>(
        `${this.apiUrl}/admin/reset-integration/history`,
        { params }
      )
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch reset history');
    }

    return response.data;
  }
}
